import React from "react";
import DateRangePicker from "react-bootstrap-daterangepicker";
import "bootstrap-daterangepicker/daterangepicker.css";
import { DashboardPeriodo } from "../../../types/dashboard";
import { formatPeriodLabel, shiftReferenceDate, toISODate } from "./periodUtils";

export interface DashboardFilterValue {
  periodo: DashboardPeriodo;
  referenceDate: Date;
  customStart: string | null; // YYYY-MM-DD
  customEnd: string | null; // YYYY-MM-DD
}

type Granularidade = Exclude<DashboardPeriodo, "personalizado">;

interface Props {
  value: DashboardFilterValue;
  onChange: (value: DashboardFilterValue) => void;
  granularidades?: { key: Granularidade; label: string }[];
}

const DEFAULT_GRANULARIDADES: { key: Granularidade; label: string }[] = [
  { key: "diario", label: "Dia" },
  { key: "semanal", label: "Semana" },
  { key: "mensal", label: "Mês" },
  { key: "semestral", label: "Semestre" },
  { key: "anual", label: "Ano" },
];

const toDisplayDate = (iso: string) => iso.split("-").reverse().join("/");

const DashboardPeriodFilter: React.FC<Props> = ({
  value,
  onChange,
  granularidades = DEFAULT_GRANULARIDADES,
}) => {
  const { periodo, referenceDate, customStart, customEnd } = value;
  const isCustom = periodo === "personalizado";

  const selectGranularidade = (key: Granularidade) => {
    onChange({ periodo: key, referenceDate: new Date(), customStart: null, customEnd: null });
  };

  const navigate = (direction: 1 | -1) => {
    onChange({ ...value, referenceDate: shiftReferenceDate(periodo, referenceDate, direction) });
  };

  const handleCustomRange = (start?: any, end?: any) => {
    if (!start || !end) return;
    const startDate = typeof start.format === "function" ? start.format("YYYY-MM-DD") : toISODate(new Date(start));
    const endDate = typeof end.format === "function" ? end.format("YYYY-MM-DD") : toISODate(new Date(end));
    onChange({ periodo: "personalizado", referenceDate, customStart: startDate, customEnd: endDate });
  };

  const rangeLabel = customStart && customEnd
    ? `${toDisplayDate(customStart)} - ${toDisplayDate(customEnd)}`
    : "Personalizado";

  const pickerInitialSettings = {
    startDate: customStart ? new Date(`${customStart}T00:00:00`) : new Date(new Date().setDate(new Date().getDate() - 7)),
    endDate: customEnd ? new Date(`${customEnd}T00:00:00`) : new Date(),
    maxDate: new Date(),
    autoUpdateInput: !!(customStart && customEnd),
    locale: {
      format: "DD/MM/YYYY",
      separator: " - ",
      applyLabel: "Aplicar",
      cancelLabel: "Cancelar",
      weekLabel: "S",
      customRangeLabel: "Personalizado",
      daysOfWeek: ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"],
      monthNames: ["Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho", "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"],
      firstDay: 1,
    },
  };

  return (
    <div className="d-flex align-items-center flex-wrap row-gap-2 column-gap-2">
      {!isCustom && (
        <div className="dash-filter-nav">
          <button
            type="button"
            className="dash-filter-nav-btn"
            onClick={() => navigate(-1)}
            aria-label="Período anterior"
          >
            <i className="ti ti-chevron-left" />
          </button>
          <span className="dash-filter-label">{formatPeriodLabel(periodo, referenceDate)}</span>
          <button
            type="button"
            className="dash-filter-nav-btn"
            onClick={() => navigate(1)}
            aria-label="Próximo período"
          >
            <i className="ti ti-chevron-right" />
          </button>
        </div>
      )}
      <div className="dash-filter-group">
        {granularidades.map((g) => (
          <button
            key={g.key}
            type="button"
            className={`dash-filter-btn ${periodo === g.key ? "active" : ""}`}
            onClick={() => selectGranularidade(g.key)}
          >
            {g.label}
          </button>
        ))}
        {/*
          react-bootstrap-daterangepicker não é totalmente controlado: ele só lê
          `initialSettings` na primeira montagem do plugin jQuery interno, então
          se `customStart`/`customEnd` mudarem externamente (ex.: resetados ao
          trocar pra "Mês" e depois voltar pra "Personalizado"), o picker não se
          resincroniza sozinho - fica mostrando/aplicando o intervalo antigo.
          Forçamos remount com uma `key` amarrada ao valor atual pra garantir
          que o picker sempre reflita o estado real do React.
        */}
        <DateRangePicker
          key={`${customStart ?? 'none'}_${customEnd ?? 'none'}`}
          initialSettings={pickerInitialSettings}
          onCallback={handleCustomRange}
        >
          <button type="button" className={`dash-filter-btn dash-filter-btn--custom ${isCustom ? "active" : ""}`}>
            <i className="ti ti-calendar-event" /> {rangeLabel}
          </button>
        </DateRangePicker>
      </div>
    </div>
  );
};

export default DashboardPeriodFilter;
